import { computed, ref } from 'vue';

/** 登录成功后顶栏灌水中（关弹窗 → 骨架 → 静默刷新） */
const authHydrating = ref(false);

/** @type {import('vue').Ref<string|null>} */
const pendingSoftRedirect = ref(null);

/** @type {Set<() => void | Promise<void>>} */
const softRehydrateHandlers = new Set();

/**
 * App 注册：钱包切号等仍保持登录态时的静默灌水（刷新余额/未读等）。
 * @param {() => void | Promise<void>} handler
 */
export function registerAuthSoftRehydrate(handler) {
  if (typeof handler !== 'function') return () => {};
  softRehydrateHandlers.add(handler);
  return () => softRehydrateHandlers.delete(handler);
}

/**
 * 顶栏骨架 + 跑完所有 soft rehydrate handlers（不整页刷新）。
 */
export async function runAuthSoftRehydrate() {
  authHydrating.value = true;
  try {
    await Promise.all(
      [...softRehydrateHandlers].map((fn) =>
        Promise.resolve()
          .then(() => fn())
          .catch((err) => {
            console.warn('[auth] soft rehydrate handler failed', err);
          })
      )
    );
  } finally {
    authHydrating.value = false;
  }
}

/**
 * 登录成功后的软灌水状态：跨 AuthDialog destroy-on-close 保留。
 */
export function useAuthHydration() {
  const isAuthHydrating = computed(() => authHydrating.value);

  /**
   * @param {string|null|undefined} [redirect]
   */
  function beginAuthHydration(redirect) {
    authHydrating.value = true;
    const path = String(redirect ?? '').trim();
    pendingSoftRedirect.value = path || null;
  }

  function endAuthHydration() {
    authHydrating.value = false;
  }

  function clearAuthHydration() {
    authHydrating.value = false;
    pendingSoftRedirect.value = null;
  }

  /** @returns {string|null} */
  function consumeSoftRedirect() {
    const path = pendingSoftRedirect.value;
    pendingSoftRedirect.value = null;
    return path;
  }

  return {
    authHydrating: isAuthHydrating,
    beginAuthHydration,
    endAuthHydration,
    clearAuthHydration,
    consumeSoftRedirect,
  };
}
